import { useRef, useState } from 'react'
import { FiMonitor } from 'react-icons/fi'
import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query'

import {
  Box,
  Button,
  Stack,
  Icon,
  Text,
  Flex,
  Divider,
  Skeleton,
  useToast
} from '@chakra-ui/react'
import { FormHandles, SubmitHandler } from '@unform/core'
import { AxiosError } from 'axios'
import * as yup from 'yup'

import { useCan } from '../../hooks/useCan'
import { useStates } from '../../hooks/useState'
import { api } from '../../services/api'
import { formatYupError } from '../../utils/formatYupError'
import { ConfirmationModal } from '../ConfirmationModal'
import { Form } from '../Form/Form'
import { Select } from '../Form/Select'
import { Modal } from '../Modal'
import { ServiceItem } from './ServiceItem'

type Location = {
  id: string
  name: string
}

type UseOnlineServiceResult = {
  services: Location[]
}

type FormData = {
  stateId: string
}

export function useOnlineService(
  options?: UseQueryOptions<UseOnlineServiceResult, AxiosError>
): UseQueryResult<UseOnlineServiceResult, AxiosError> {
  return useQuery<UseOnlineServiceResult, AxiosError>(
    ['me-online-services'],
    async () => {
      const { data } = await api.get<Location[]>(
        `/lawyer/profile/attendance/online`
      )

      return { services: data }
    },
    {
      staleTime: 1000 * 60 * 1, // One minutes
      ...options
    }
  )
}

const onlineServiceFormSchema = yup.object({
  stateId: yup.string().required('O estado é obrigatório.')
})

export function OnlineService(): JSX.Element {
  const { data, isLoading, error, refetch } = useOnlineService()
  const [isOpen, setIsOpen] = useState(false)
  const [deletingOnlineService, setDeletingOnlineService] = useState('')
  const can = useCan('fields', data?.services)
  const formRef = useRef<FormHandles>(null)
  const { data: states } = useStates()
  const toast = useToast({
    duration: 5000,
    isClosable: true,
    position: 'top-right'
  })

  const handleSubmit: SubmitHandler<FormData> = async data => {
    try {
      await onlineServiceFormSchema.validate(data, { abortEarly: false })
      await api.post(`/lawyer/profile/attendance/online/${data.stateId}`)
      refetch()
      setIsOpen(false)
      toast({
        status: 'success',
        description: 'Atendimento online criado com sucesso!'
      })
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        formRef.current?.setErrors(formatYupError(error))
      } else {
        toast({
          status: 'error',
          description: 'Ocorreu um erro ao tentar criar um atendimento online'
        })
      }
    }
  }

  async function handleDeleteOnlineService(): Promise<void> {
    try {
      await api.delete(
        `/lawyer/profile/attendance/online/${deletingOnlineService}`
      )
      setDeletingOnlineService('')
      refetch()
      toast({
        status: 'success',
        description: 'Atendimento online removido com sucesso!'
      })
    } catch (error) {
      toast({
        status: 'error',
        description: 'Ocorreu um erro ao excluir um atendimento online!'
      })
    }
  }

  function handleOpenModal(): void {
    if (can) {
      setIsOpen(true)
    } else {
      toast({
        status: 'warning',
        title: 'Você não tem permissão!',
        description: 'Seu plano limita a criação de atendimentos online.'
      })
    }
  }

  return (
    <>
      <Box mt="4">
        <Stack direction={['column', 'column', 'row']} align="start">
          <Icon as={FiMonitor} color="green.300" w="9" h="9" p="1" />
          <Box w="full">
            <Flex
              justify="space-between"
              align={['flex-start', 'flex-start', 'center']}
              direction={['column', 'column', 'row']}
            >
              <Text as="h4" fontSize="xl">
                Atendimento online
              </Text>
              <Button
                variant="link"
                colorScheme="green"
                fontWeight="medium"
                onClick={handleOpenModal}
              >
                Adicionar estado
              </Button>
            </Flex>
            <Divider my="4" />
            <Flex flexWrap="wrap" gridGap="4">
              {error ? (
                <Text color="red.400" fontWeight="medium">
                  Não conseguimos carregar seus dados
                </Text>
              ) : !isLoading && data ? (
                data.services.length === 0 ? (
                  <Text fontWeight="medium">
                    Você não possui atendimentos online cadastrados.
                  </Text>
                ) : (
                  data.services?.map(item => (
                    <ServiceItem
                      key={item.id}
                      label={item.name}
                      onDelete={() => setDeletingOnlineService(item.id)}
                    />
                  ))
                )
              ) : (
                <>
                  <Skeleton w="140px" h="32px" />
                  <Skeleton w="140px" h="32px" />
                  <Skeleton w="140px" h="32px" />
                </>
              )}
            </Flex>
          </Box>
        </Stack>
      </Box>
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Atendimento online"
      >
        <Form
          ref={formRef}
          onSubmit={handleSubmit}
          d="flex"
          flexDirection="column"
          alignItems="flex-end"
        >
          <Select
            name="stateId"
            label="Estado"
            options={states?.states || []}
            placeholder="Selecione um estado"
          />
          <Button
            colorScheme="yellow"
            mt="4"
            onClick={() => formRef.current?.submitForm()}
          >
            Cadastrar
          </Button>
        </Form>
      </Modal>
      <ConfirmationModal
        title="Excluir atendimento online?"
        isOpen={!!deletingOnlineService}
        onClose={() => setDeletingOnlineService('')}
        onAccept={handleDeleteOnlineService}
      />
    </>
  )
}
